// === Task 31 === Pure analyzer for the Flow Viewer.
// Walks every section's workflow (triggers + nested actions) and returns a
// per-scene map of which variables that scene READS (referenced in branch
// conditions / setVar expressions) and WRITES (setVar targets), plus any
// outbound scene jumps. Pure — no Redux, no React, no side effects — so it
// is trivially unit-testable and can be reused by Show All, Show Scene and
// Storyboard modes without re-computing.

const IDENT_RE = /\b[a-z][a-zA-Z0-9_]*\b/g;

function collectIdentsFromCondition(condStr, knownVars) {
  if (!condStr || typeof condStr !== "string") return [];
  const hits = condStr.match(IDENT_RE) || [];
  return hits.filter((n) => knownVars.has(n) && n !== "true" && n !== "false");
}

function walkActions(actions, ctx) {
  for (const a of actions || []) {
    if (!a || typeof a !== "object") continue;
    if (a.kind === "setVar" && a.varId) ctx.writes.add(a.varId);
    if (a.kind === "jumpScene" && a.target) ctx.jumps.add(a.target);
    if (a.kind === "branch") {
      for (const v of collectIdentsFromCondition(a.condition, ctx.knownVars)) {
        ctx.reads.add(v);
      }
      walkActions(a.whenTrue, ctx);
      walkActions(a.whenFalse, ctx);
    }
    // setVar with an expression value (rare) — read the referenced vars.
    if (a.kind === "setVar" && a.value && typeof a.value === "object" && a.value.expr) {
      for (const v of collectIdentsFromCondition(a.value.expr, ctx.knownVars)) {
        ctx.reads.add(v);
      }
    }
  }
}

export function analyzeWorkflow(navItemsById, sections) {
  const perScene = {};
  const allVars = [];

  for (const section of sections || []) {
    if (!section || !section.workflow) continue;
    const wf = section.workflow;
    const knownVars = new Set((wf.variables || []).map((v) => v.id));
    for (const v of wf.variables || []) {
      allVars.push({ id: v.id, type: v.type, sectionId: section.id });
    }
    for (const trigger of wf.triggers || []) {
      const sceneId = trigger.sceneId === "*" ? section.id : trigger.sceneId;
      if (!sceneId) continue;
      perScene[sceneId] = perScene[sceneId] || {
        reads: new Set(),
        writes: new Set(),
        jumps: new Set(),
      };
      const ctx = {
        reads: perScene[sceneId].reads,
        writes: perScene[sceneId].writes,
        jumps: perScene[sceneId].jumps,
        knownVars,
      };
      walkActions(trigger.actions, ctx);
    }
  }

  const finalPerScene = {};
  for (const [sId, s] of Object.entries(perScene)) {
    finalPerScene[sId] = {
      reads: Array.from(s.reads).sort(),
      writes: Array.from(s.writes).sort(),
      outboundJumps: Array.from(s.jumps).sort(),
    };
  }

  // Canonical scene order: section.children, filtered to non-section navItems.
  const scenesByOrder = [];
  for (const section of sections || []) {
    if (section && Array.isArray(section.children)) {
      for (const childId of section.children) {
        const child = navItemsById && navItemsById[childId];
        if (child && child.type !== "section") {
          scenesByOrder.push(childId);
        }
      }
    }
  }

  return { perScene: finalPerScene, scenesByOrder, variables: allVars };
}

export default analyzeWorkflow;
